import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Button, FoodsIconButton, FoodsTextInput, Typography } from "@foodpilot/foods";
import { alpha, Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useOpenFoodFactsResolve } from "src/api/client-api/openFoodFacts/useOpenFoodFactsResolve";
import { useOpenFoodFactsSearch } from "src/api/client-api/openFoodFacts/useOpenFoodFactsSearch";
import { OpenFoodFactsResolvedProduct, OpenFoodFactsSearchItem } from "src/api/client-api/openFoodFacts/types";
import openFoodFactsLogo from "src/assets/images/logo-open-food-facts-avatar.webp";
import { FoodsBottomSheetDialog } from "src/components/Dialog/FoodsBottomSheetDialog";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { ReferencePagination } from "src/components/ReferenceSearch";

type OpenFoodFactsSearchModalProps = {
    open: boolean;
    initialQuery: string;
    onClose: () => void;
    onSelect: (product: OpenFoodFactsResolvedProduct) => Promise<void> | void;
};

type OpenFoodFactsSearchBodyProps = {
    initialQuery: string;
    onClose: () => void;
    onSelect: (product: OpenFoodFactsResolvedProduct) => Promise<void> | void;
};

const PAGE_SIZE = 12;
const noopSetOpen = () => undefined;

export const OpenFoodFactsSearchModal = (props: OpenFoodFactsSearchModalProps) => {
    const { open, initialQuery, onClose, onSelect } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const title = t("product.openFoodFacts.search.title", "Search Open Food Facts");
    const closeLabel = t("common.close", "Close");
    const header = (
        <Box
            component="header"
            sx={{
                alignItems: "center",
                boxSizing: "border-box",
                display: "flex",
                height: theme.spacing(9.5),
                justifyContent: "space-between",
                minWidth: 0,
                padding: theme.spacing(2),
                width: "100%",
            }}
        >
            <Stack alignItems="center" direction="row" gap={theme.spacing(1.5)} minWidth={0}>
                <Box
                    component="img"
                    src={openFoodFactsLogo}
                    alt=""
                    height={theme.spacing(6)}
                    width={theme.spacing(6)}
                />
                <Typography
                    component="span"
                    variant="h5"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                    {title}
                </Typography>
            </Stack>
            <FoodsIconButton
                icon="close"
                size="m"
                buttonProps={{ "aria-label": closeLabel, onClick: onClose, type: "button" }}
            />
        </Box>
    );

    return (
        <FoodsBottomSheetDialog
            open={open}
            onClose={onClose}
            setOpen={noopSetOpen}
            title={header}
            content={
                open && (
                    <OpenFoodFactsSearchBody
                        key={initialQuery}
                        initialQuery={initialQuery}
                        onClose={onClose}
                        onSelect={onSelect}
                    />
                )
            }
            PaperProps={{
                "aria-label": title,
                sx: {
                    borderRadius: theme.spacing(1.5),
                    margin: theme.spacing(2),
                    maxHeight: `min(${theme.spacing(91)}, calc(100dvh - ${theme.spacing(4)}))`,
                    maxWidth: theme.spacing(112.5),
                    overflow: "hidden",
                    width: `calc(100% - ${theme.spacing(4)})`,
                    "& .MuiDialogTitle-root": {
                        borderBottom: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                        boxSizing: "border-box",
                        height: theme.spacing(9.5),
                        minHeight: theme.spacing(9.5),
                        padding: 0,
                    },
                    "& .MuiDialogTitle-root > .MuiButton-root": { display: "none" },
                },
            }}
        />
    );
};

const OpenFoodFactsSearchBody = (props: OpenFoodFactsSearchBodyProps) => {
    const { initialQuery, onClose, onSelect } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [query, setQuery] = useState(initialQuery);
    const [committedQuery, setCommittedQuery] = useState("");
    const [hasCommittedSearch, setHasCommittedSearch] = useState(false);
    const [page, setPage] = useState(1);
    const [selectedCode, setSelectedCode] = useState<string | null>(null);
    const [awaitingFreshResults, setAwaitingFreshResults] = useState(false);
    const search = useOpenFoodFactsSearch({
        query: committedQuery,
        page,
        pageSize: PAGE_SIZE,
        enabled: hasCommittedSearch,
    });
    const resolve = useOpenFoodFactsResolve();

    useEffect(() => {
        if (awaitingFreshResults && !search.isFetching) {
            setAwaitingFreshResults(false);
        }
    }, [awaitingFreshResults, search.isFetching]);

    const isInputStale = query.trim() !== committedQuery;
    const activeSearchData = hasCommittedSearch && !isInputStale && !awaitingFreshResults ? search.data : undefined;

    const selectedItem = useMemo(
        () => activeSearchData?.items.find((item) => item.code === selectedCode) ?? null,
        [activeSearchData?.items, selectedCode],
    );

    const pageCount = Math.max(1, Math.ceil((activeSearchData?.total ?? 0) / PAGE_SIZE));
    const hasTypedSearchCriteria = query.trim().length >= 2;
    const hasCommittedSearchCriteria = committedQuery.trim().length >= 2;
    const isSearchPending = !isInputStale && search.isFetching;
    const hasVisibleResults = Boolean(activeSearchData?.items.length);
    const shouldShowFullPageLoader = hasCommittedSearchCriteria && !isInputStale && awaitingFreshResults;
    const showNoResults =
        hasCommittedSearchCriteria &&
        !isInputStale &&
        !isSearchPending &&
        !search.isError &&
        !activeSearchData?.items.length;
    const showSearchError = hasCommittedSearchCriteria && !isInputStale && search.isError;

    const commitSearch = useCallback(() => {
        const nextQuery = query.trim();
        const hasSearchCriteria = nextQuery.length >= 2;

        resolve.reset();
        setCommittedQuery(nextQuery);
        setHasCommittedSearch(hasSearchCriteria);
        setPage(1);
        setSelectedCode(null);
        if (hasSearchCriteria) {
            setAwaitingFreshResults(true);
        }
    }, [query, resolve]);

    const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") {
            return;
        }

        event.preventDefault();
        commitSearch();
    };

    const handleQueryChange = (value: string) => {
        setQuery(value);
        setSelectedCode(null);
        resolve.reset();
    };

    const handlePageChange = (value: number) => {
        setPage(value);
        setSelectedCode(null);
        resolve.reset();
    };

    const handleSelect = async () => {
        if (selectedCode === null) {
            return;
        }

        try {
            const resolved = await resolve.mutateAsync(selectedCode);
            await onSelect(resolved);
        } catch {
            // Error state is surfaced via resolve.isError below.
        }
    };

    return (
        <Stack minHeight={0}>
            <Box sx={{ minHeight: 0, overflowY: "auto", padding: theme.spacing(2) }}>
                <Stack gap={theme.spacing(2)}>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                        <Box flex={1}>
                            <FoodsTextInput
                                value={query}
                                onChange={handleQueryChange}
                                ExtraInputProps={{
                                    placeholder: t("product.openFoodFacts.search.productPlaceholder", "Product name"),
                                    onKeyDown: handleSearchKeyDown,
                                    inputProps: {
                                        "aria-label": t(
                                            "product.openFoodFacts.search.productPlaceholder",
                                            "Product name",
                                        ),
                                    },
                                }}
                            />
                        </Box>
                        <Button
                            variant="primary"
                            onClick={commitSearch}
                            disabled={!hasTypedSearchCriteria || search.isFetching}
                        >
                            {t("common.search", "Search")}
                        </Button>
                    </Stack>

                    {shouldShowFullPageLoader ?
                        <Stack alignItems="center" gap={1} padding={3}>
                            <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                            <Typography variant="body2" color="text.secondary">
                                {t("common.loading", "Loading...")}
                            </Typography>
                        </Stack>
                    : hasVisibleResults ?
                        <>
                            {isSearchPending && (
                                <Stack alignItems="flex-end">
                                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                </Stack>
                            )}
                            <ResultsTable
                                items={activeSearchData?.items ?? []}
                                selectedCode={selectedCode}
                                onSelect={(item) => {
                                    setSelectedCode(item.code);
                                    resolve.reset();
                                }}
                            />

                            <Stack alignItems="center" paddingTop={theme.spacing(1)}>
                                <ReferencePagination
                                    disabled={isSearchPending}
                                    onPageChange={handlePageChange}
                                    page={page}
                                    pageCount={pageCount}
                                />
                            </Stack>
                        </>
                    : showNoResults ?
                        <Typography variant="body2" color="text.secondary">
                            {t("product.openFoodFacts.search.noResults", "No results")}
                        </Typography>
                    : showSearchError ?
                        <Typography variant="body2" color="error">
                            {t(
                                "product.openFoodFacts.search.error",
                                "Open Food Facts search failed. Please try again.",
                            )}
                        </Typography>
                    : resolve.isError ?
                        <Typography variant="body2" color="error">
                            {t(
                                "product.openFoodFacts.resolve.error",
                                "Could not load this Open Food Facts product. Please select another one.",
                            )}
                        </Typography>
                    : hasTypedSearchCriteria && isInputStale ?
                        <Typography variant="body2" color="text.secondary">
                            {t("product.openFoodFacts.search.waitingForSubmit", "Click Search to launch the search.")}
                        </Typography>
                    : !hasTypedSearchCriteria ?
                        <Typography variant="body2" color="text.secondary">
                            {t(
                                "product.openFoodFacts.search.minLength",
                                "Enter at least 2 characters in the product name.",
                            )}
                        </Typography>
                    :   null}
                </Stack>
            </Box>

            <Stack
                component="footer"
                direction="row"
                flexShrink={0}
                gap={theme.spacing(1)}
                justifyContent="flex-end"
                padding={theme.spacing(2)}
                sx={{
                    borderTop: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                }}
            >
                <Button variant="text" onClick={onClose}>
                    {t("common.cancel", "Cancel")}
                </Button>
                <Button
                    variant="primary"
                    disabled={selectedItem === null || resolve.isLoading}
                    onClick={handleSelect}
                    startIcon={
                        resolve.isLoading ?
                            <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                        :   undefined
                    }
                >
                    {t("product.openFoodFacts.selectProduct", "Select this product")}
                </Button>
            </Stack>
        </Stack>
    );
};

type ResultsTableProps = {
    items: OpenFoodFactsSearchItem[];
    selectedCode: string | null;
    onSelect: (item: OpenFoodFactsSearchItem) => void;
};

const ResultsTable = ({ items, selectedCode, onSelect }: ResultsTableProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const divider = `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`;
    const cellSx = {
        borderBottom: divider,
        boxSizing: "border-box",
        padding: theme.spacing(1, 1.5),
        textAlign: "left",
        verticalAlign: "middle",
    } as const;

    return (
        <Box sx={{ maxHeight: theme.spacing(54), overflowX: "auto", overflowY: "auto", width: "100%" }}>
            <Box
                component="table"
                sx={{ borderCollapse: "collapse", minWidth: theme.spacing(92), tableLayout: "fixed", width: "100%" }}
            >
                <Box component="thead" sx={{ backgroundColor: theme.custom.grey[200], position: "sticky", top: 0 }}>
                    <Box component="tr">
                        <Box component="th" scope="col" sx={{ ...cellSx, width: theme.spacing(9) }}>
                            <Typography variant="body-semibold">{t("product.openFoodFacts.image", "Image")}</Typography>
                        </Box>
                        <Box component="th" scope="col" sx={cellSx}>
                            <Typography variant="body-semibold">
                                {t("product.openFoodFacts.product", "Product")}
                            </Typography>
                        </Box>
                        <Box component="th" scope="col" sx={{ ...cellSx, width: theme.spacing(20) }}>
                            <Typography variant="body-semibold">{t("product.openFoodFacts.brand", "Brand")}</Typography>
                        </Box>
                        <Box component="th" scope="col" sx={{ ...cellSx, width: theme.spacing(16) }}>
                            <Typography variant="body-semibold">
                                {t("product.openFoodFacts.quantity", "Quantity")}
                            </Typography>
                        </Box>
                        <Box
                            component="th"
                            scope="col"
                            sx={{ ...cellSx, textAlign: "right", width: theme.spacing(10) }}
                        >
                            <Typography variant="body-semibold">
                                {t("product.openFoodFacts.details", "Details")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box component="tbody">
                    {items.map((item) => (
                        <ResultRow
                            key={item.code}
                            item={item}
                            selected={selectedCode === item.code}
                            onSelect={() => onSelect(item)}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

type ResultRowProps = {
    item: OpenFoodFactsSearchItem;
    selected: boolean;
    onSelect: () => void;
};

const ResultRow = (props: ResultRowProps) => {
    const { item, selected, onSelect } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const divider = `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`;
    const cellSx = {
        borderBottom: divider,
        boxSizing: "border-box",
        height: theme.spacing(8),
        padding: theme.spacing(1, 1.5),
        verticalAlign: "middle",
    } as const;

    return (
        <Box
            component="tr"
            onClick={onSelect}
            sx={{
                backgroundColor: selected ? theme.palette.action.selected : alpha(theme.palette.action.selected, 0),
                cursor: "pointer",
                "&:hover": { backgroundColor: selected ? theme.palette.action.selected : theme.palette.action.hover },
            }}
        >
            <Box component="td" sx={{ ...cellSx, width: theme.spacing(9) }}>
                {item.imageUrl ?
                    <Box
                        component="img"
                        src={item.imageUrl}
                        alt=""
                        sx={{
                            width: theme.spacing(6),
                            height: theme.spacing(6),
                            objectFit: "contain",
                            borderRadius: 1,
                        }}
                    />
                :   null}
            </Box>
            <Box component="td" sx={cellSx}>
                <Typography variant="body-semibold">{item.name}</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                    {item.code}
                </Typography>
            </Box>
            <Box component="td" sx={{ ...cellSx, width: theme.spacing(20) }}>
                {item.brand ?? "-"}
            </Box>
            <Box component="td" sx={{ ...cellSx, width: theme.spacing(16) }}>
                {item.quantityLabel ?? "-"}
            </Box>
            <Box component="td" sx={{ ...cellSx, textAlign: "right", width: theme.spacing(10) }}>
                <FoodsIconButton
                    icon="openNewTab"
                    size="s"
                    buttonProps={{
                        component: "a",
                        href: `https://fr.openfoodfacts.org/produit/${item.code}`,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        "aria-label": t("product.openFoodFacts.details", "Details"),
                        onClick: (event) => event.stopPropagation(),
                    }}
                />
            </Box>
        </Box>
    );
};
